import Select, { type GroupBase, type Props } from 'react-select';

type StyledPlotSelectProps = { maxWidth?: number; height?: string };

export const StyledPlotSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  maxWidth,
  height,
  ...props
}: Props<Option, IsMulti, Group> & StyledPlotSelectProps) => {
  return (
    <Select
      classNamePrefix={props.classNamePrefix}
      isSearchable={props.isSearchable ?? false}
      styles={{
        container: (prev) => ({
          ...prev,
          ...(maxWidth
            ? { maxWidth: `${maxWidth}px`, width: '100%' }
            : { width: '100%' }),
          ...(height ? { height } : {}),
          fontSize: '12px',
          fontWeight: 400,
        }),
        control: (prev) => ({
          ...prev,
          minHeight: height || '24px',
          height: '100%',
          backgroundColor: '#F1F5F9',
          borderWidth: 0,
          borderColor: 'none',
          boxShadow: 'none',
        }),
        valueContainer: (prev) => ({
          ...prev,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          overflow: 'hidden',
        }),
        placeholder: (prev) => ({
          ...prev,
          margin: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          pointerEvents: 'none',
        }),
        indicatorsContainer: (prev) => ({
          ...prev,
          height: '100%',
          svg: { width: '12px', height: '12px' },
        }),
        clearIndicator: (prev) => ({
          ...prev,
          color: '#A0AEC0',
          padding: '4px',
          cursor: 'pointer',
          ' svg': {},
          ':hover': {
            color: '#718096',
          },
        }),
        indicatorSeparator: (prev) => ({
          ...prev,
          display: 'none',
        }),
        dropdownIndicator: (prev) => ({
          ...prev,
          color: '#2D426A',
          ' svg': {
            width: '12px',
            height: '12px',
          },
        }),
        menu: (prev) => ({
          ...prev,
          borderRadius: 0,
          ...(maxWidth
            ? { minWidth: `${maxWidth}px` }
            : { width: 'fit-content', minWidth: '100px' }),
          position: 'absolute',
          left: 0,
          right: 0,
          zIndex: 3,
        }),
        group: (prev) => ({
          ...prev,
          paddingBottom: 0,
          marginBottom: 0,
        }),
        groupHeading: (prev) => ({
          ...prev,
          padding: '0 .5rem',
        }),
        option: (prev, { isSelected, isFocused }) => {
          const backgroundColor = (() => {
            if (isSelected) return '#3182ce';
            if (isFocused) return '#EDF2F7';
            return 'transparent';
          })();
          return {
            ...prev,
            backgroundColor,
            color: isSelected ? '#fff' : '#2D426A',
            fontSize: '12px',
            fontWeight: 400,
            padding: '8px 12px',
            minHeight: '32px',
            lineHeight: '16px',
            cursor: 'pointer',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            ...(isSelected && { ':hover': { backgroundColor: '#2970b2' } }),
          };
        },
        singleValue: (prev) => ({
          ...prev,
          height: '100%',
          lineHeight: '100%',
        }),
        multiValue: (prev) => ({
          ...prev,
          fontSize: '11px',
          margin: '2px 4px 2px 0',
        }),
        multiValueLabel: (prev) => ({
          ...prev,
          fontSize: '11px',
          padding: '4px',
          lineHeight: '1.1',
        }),
        multiValueRemove: (prev) => ({
          ...prev,
          fontSize: '11px',
          padding: '2px 4px',
        }),
        input: (prev) => ({
          ...prev,
          height: '20px',
          margin: 0,
          padding: 0,
          '> .metric-switch__input': {
            height: '20px',
          },
        }),
      }}
      {...props}
    />
  );
};
